import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { supabase } from '../lib/supabase_client';
import { useCompany } from '../contexts/CompanyContext';
import { useSession } from '../contexts/SessionContext';
import { MainLayout } from './MainLayout';
import '../styles/company-profile.css';
import '../styles/page-base.css';
import { ResetPasswordModal } from './ResetPasswordModal';

interface CompanyInfo {
  company_name?: string;
  address?: string;
}

interface ConversionPreferences {
  corporate_information?: boolean;
  note_accounting_policy?: boolean;
  sopl_detail?: boolean;
}

interface StepFunctionPreferences {
  use_first_page_fs?: boolean;
  combine_accounting_policies?: boolean;
  allow_expense_injection?: boolean;
}

// Convert UI format to Step Function format
function convertUIToStepFunctionFormat(uiPrefs: ConversionPreferences): StepFunctionPreferences {
  return {
    use_first_page_fs: uiPrefs.corporate_information || false,
    combine_accounting_policies: uiPrefs.note_accounting_policy || false,
    allow_expense_injection: uiPrefs.sopl_detail || false
  };
}

// Convert Step Function format to UI format
function convertStepFunctionToUIFormat(sfPrefs: StepFunctionPreferences): ConversionPreferences {
  return {
    corporate_information: sfPrefs.use_first_page_fs || false,
    note_accounting_policy: sfPrefs.combine_accounting_policies || false,
    sopl_detail: sfPrefs.allow_expense_injection || false
  };
}

export default function CompanyProfile() {
  const { companyId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { selectedCompany, setSelectedCompany, userCompanies, setUserCompanies } = useCompany();
  const { user, permissions } = useSession();  
  const [isResetPasswordModalOpen, setIsResetPasswordModalOpen] = useState(false);

  const [companyInfo, setCompanyInfo] = useState<CompanyInfo>({});
  const [conversionPreferences, setConversionPreferences] = useState<ConversionPreferences>({
    corporate_information: false,
    note_accounting_policy: false,
    sopl_detail: false
  });
  const [loading, setLoading] = useState(true);
  const [orgCountryIso, setOrgCountryIso] = useState<string | null>(null);
  const [isEditingDetails, setIsEditingDetails] = useState(false);
  const [editName, setEditName] = useState('');
  const [editAddress, setEditAddress] = useState('');
  const [savingDetails, setSavingDetails] = useState(false);
  const [detailsMsg, setDetailsMsg] = useState<string | null>(null);

  const fetchCompanyProfileData = useCallback(async () => {
    // Use selectedCompany first, fallback to companyId from URL
    const activeCompanyId = selectedCompany?.company_id || companyId;
    if (!activeCompanyId) return;

    setLoading(true);
    try {
      // Fetch organization data
      const { data: orgData, error: orgError } = await supabase
        .from('organizations')
        .select('id, name, address, preferences, country_iso')
        .eq('id', activeCompanyId)
        .maybeSingle();

      if (!orgError && orgData) {
        setOrgCountryIso(orgData.country_iso || null);


        if (orgData.preferences) {
          const uiPrefs = convertStepFunctionToUIFormat(orgData.preferences as StepFunctionPreferences);
          setConversionPreferences(uiPrefs);
        }

        setCompanyInfo({
          company_name: orgData.name || selectedCompany?.company_name || '',
          address: orgData.address || ''
        });
      }
    } catch (error) {
      console.error('Error fetching company profile data:', error);
    } finally {
      setLoading(false);
    }
  }, [companyId, selectedCompany?.company_id]);

  useEffect(() => {
    const activeCompanyId = selectedCompany?.company_id || companyId;
    if (activeCompanyId) {
      fetchCompanyProfileData();
    }
  }, [companyId, selectedCompany?.company_id, fetchCompanyProfileData]);
  
  useEffect(() => {
    const activeId = selectedCompany?.company_id;
    if (!activeId) return;
    if (companyId === activeId) return;
  
    if (location.pathname.startsWith('/settings/')) {
      navigate(`/settings/${activeId}`, { replace: true });
    } else if (location.pathname.startsWith('/company-profile/')) {
      navigate(`/company-profile/${activeId}`, { replace: true });
    }
  }, [selectedCompany?.company_id, companyId, location.pathname, navigate]);
  
  const handleTogglePreference = async (key: keyof ConversionPreferences) => {
    const newUIPreferences = {
      ...conversionPreferences,
      [key]: !conversionPreferences[key]
    };
    setConversionPreferences(newUIPreferences);

    // Save to database
    const activeCompanyId = selectedCompany?.company_id || companyId;
    if (!activeCompanyId) return;

    try {
      // Convert UI format to Step Function format before saving
      const stepFunctionPrefs = convertUIToStepFunctionFormat(newUIPreferences);
      
      await supabase
        .from('organizations')
        .update({ preferences: stepFunctionPrefs })
        .eq('id', activeCompanyId);
    } catch (error) {
      console.error('Error updating preferences:', error);
      // Revert on error
      setConversionPreferences(conversionPreferences);
    }
  };

  const startEditDetails = () => {
    setEditName(companyInfo.company_name || '');
    setEditAddress(companyInfo.address || '');
    setDetailsMsg(null);
    setIsEditingDetails(true);
  };

  const cancelEditDetails = () => {
    setIsEditingDetails(false);
    setDetailsMsg(null);
  };

  const saveCompanyDetails = async () => {
    const activeCompanyId = selectedCompany?.company_id || companyId;
    const nextName = editName.trim();
    const nextAddress = editAddress.trim();

    if (!activeCompanyId) return;
    if (!nextName) {
      setDetailsMsg('Company name is required.');
      return;
    }

    setSavingDetails(true);
    setDetailsMsg(null);
    try {
      const { error } = await supabase
        .from('organizations')
        .update({ name: nextName, address: nextAddress || null })
        .eq('id', activeCompanyId);

      if (error) throw error;

      setCompanyInfo({ company_name: nextName, address: nextAddress });
      setIsEditingDetails(false);

      if (selectedCompany?.company_id === activeCompanyId) {
        const updatedCompany = {
          ...selectedCompany,
          company_name: nextName,
          address: nextAddress,
        };
        setSelectedCompany(updatedCompany);
        setUserCompanies(
          userCompanies.map((c) =>
            c.company_id === activeCompanyId
              ? { ...c, company_name: nextName, address: nextAddress }
              : c
          )
        );
      }
    } catch (error: any) {
      console.error('Error updating company details:', error);
      setDetailsMsg(error?.message || 'Failed to save company details.');
    } finally {
      setSavingDetails(false);
    }
  };

  const handleDownloadNDA = () => {
    // Download NDA document from public folder
    const link = document.createElement('a');
    link.href = '/NDA.pdf';
    link.download = 'NDA.pdf';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <MainLayout>
      <div className="company-profile-container">
      <div className="max-w-6xl mx-auto space-y-6">
        {/* Header - Matching AllFilings/Dashboard style */}
        <div className="company-profile-header">
          <div className="company-profile-header-left">
            <h1 className="company-profile-title">Settings</h1>
          </div>
          <div className="company-profile-header-right">
            {/* Add any action buttons here if needed in the future */}
          </div>
        </div>

        {/* Main Content - 2x2 Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Account Section */}
          <div className="company-profile-card">
            <div className="flex items-start justify-between gap-4 mb-4">
              <div>
                <h2 className="text-xl font-bold text-blue-600 m-0">Company Details</h2>
                <p className="text-xs text-gray-500 mt-1">
                  If you do not need an LHDN e-Invoice, the information below will be used as your billing address.
                </p>
              </div>
              <div className="shrink-0 flex items-center gap-2">
                {isEditingDetails ? (
                  <>
                    <button
                      type="button"
                      onClick={cancelEditDetails}
                      disabled={savingDetails}
                      className="f-btn f-btn-soft f-btn-xs"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={saveCompanyDetails}
                      disabled={savingDetails}
                      className="f-btn f-btn-primary f-btn-xs"
                    >
                      {savingDetails ? 'Saving...' : 'Save'}
                    </button>
                  </>
                ) : (
                  <button
                    type="button"
                    onClick={startEditDetails}
                    disabled={loading}
                    className="f-btn f-btn-soft f-btn-xs inline-flex items-center gap-1.5"
                  >
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.232 5.232l3.536 3.536M4 20h4.586a1 1 0 00.707-.293l9.414-9.414a2 2 0 000-2.828l-2.172-2.172a2 2 0 00-2.828 0L4.586 15.707A1 1 0 004 16.414V20z" />
                    </svg>
                    Edit
                  </button>
                )}
              </div>
            </div>
            <div className="space-y-4">
              {detailsMsg && (
                <p className={`text-xs ${detailsMsg.includes('required') || detailsMsg.includes('Failed') ? 'text-red-600' : 'text-gray-600'}`}>
                  {detailsMsg}
                </p>
              )}
              <div className="flex justify-between items-start py-2 border-b border-gray-200 gap-4">
                <span className="text-gray-600 text-sm shrink-0">Company Name:</span>
                {isEditingDetails ? (
                  <input
                    type="text"
                    className="w-full max-w-xs border border-gray-300 rounded-lg px-3 py-1.5 text-sm text-gray-900 focus:ring-2 focus:ring-blue-500 focus:outline-none"
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                  />
                ) : (
                  <span className="text-gray-900 font-medium text-sm text-right">
                    {loading ? 'Loading...' : (companyInfo.company_name || 'N/A')}
                  </span>
                )}
              </div>
              <div className="flex justify-between items-start py-2 border-b border-gray-200 gap-4">
                <span className="text-gray-600 text-sm shrink-0">Address:</span>
                {isEditingDetails ? (
                  <textarea
                    className="w-full max-w-xs border border-gray-300 rounded-lg px-3 py-1.5 text-sm text-gray-900 focus:ring-2 focus:ring-blue-500 focus:outline-none"
                    rows={3}
                    value={editAddress}
                    onChange={(e) => setEditAddress(e.target.value)}
                  />
                ) : (
                  <span className="text-gray-900 font-medium text-sm text-right whitespace-pre-line">
                    {loading ? 'Loading...' : (companyInfo.address || 'N/A')}
                  </span>
                )}
              </div>
              <div className="flex justify-between items-center py-2 border-b border-gray-200">
                <span className="text-gray-600 text-sm">Non Disclosure Agreement:</span>
                <button
                  onClick={handleDownloadNDA}
                  className="f-btn f-btn-gray f-btn-xs"
                >
                  Download
                </button>
              </div>
              <div className="flex justify-between items-center py-2">
                <span className="text-gray-600 text-sm">Password:</span>
                <button
                  onClick={() => setIsResetPasswordModalOpen(true)}
                  className="f-btn f-btn-soft f-btn-xs"
                >
                  Reset Password
                </button>
              </div>
            </div>
          </div>

          {/* Conversion Preferences Section (MY only) */}
          {permissions?.['settings_conversion_preference'] && orgCountryIso !== 'SG' && (
          <div className="company-profile-card">
            <h2 className="text-xl font-bold text-blue-600 mb-4">Conversion Preferences</h2>
            <div className="space-y-4">
              <div className="flex items-start justify-between py-3 border-b border-gray-200">
                <div className="flex-1 pr-4">
                  <p className="text-sm text-gray-700 font-medium mb-1">
                    <strong>Corporate information</strong>
                  </p>
                  <p className="text-xs text-gray-500">
                    Disclose first page of Financial Statement instead of Note 1 in "Note - Corporate Information" disclosure text block
                  </p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={conversionPreferences.corporate_information || false}
                    onChange={() => handleTogglePreference('corporate_information')}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                </label>
              </div>

              <div className="flex items-start justify-between py-3 border-b border-gray-200">
                <div className="flex-1 pr-4">
                  <p className="text-sm text-gray-700 font-medium mb-1">
                    <strong>Note - Accounting Policy</strong>
                  </p>
                  <p className="text-xs text-gray-500">
                    Include all sub accounting policies (eg: 4.1 to 4.10) again into one combined "Significant Accounting Policies" text block
                  </p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={conversionPreferences.note_accounting_policy || false}
                    onChange={() => handleTogglePreference('note_accounting_policy')}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                </label>
              </div>

              <div className="flex items-start justify-between py-3 border-b border-gray-200">
                <div className="flex-1 pr-4">
                  <p className="text-sm text-gray-700 font-medium mb-1">
                    <strong>SOPL detail</strong>
                  </p>
                  <p className="text-xs text-gray-500">
                    Allow injection of expenses such as auditor remuneration or depreciation etc. and net off at MISC expenses
                  </p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={conversionPreferences.sopl_detail || false}
                    onChange={() => handleTogglePreference('sopl_detail')}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                </label>
              </div>
            </div>
          </div>)}

        </div>

        </div>
      </div>

      <ResetPasswordModal
        isOpen={isResetPasswordModalOpen}
        onClose={() => setIsResetPasswordModalOpen(false)}
      />

    </MainLayout>
  );
}